import { Cloud, Laptop } from 'lucide-react';
import type { ProjectPlacement } from '@volley/crucible/runtime/agent-runtime-profile.js';
import type { CloudAccountStatus } from '@volley/crucible-ipc-types/ipc-channels';

export type { ProjectPlacement };

export interface ProjectPlacementSelectorProps {
  value: ProjectPlacement;
  onChange: (placement: ProjectPlacement) => void;
  cloudStatus: CloudAccountStatus;
  onCloudSelect: () => void | Promise<void>;
  localSelectable?: boolean;
  localReason?: string;
  onOpenLocalSetup?: () => void;
  presentation?: 'default' | 'workbench';
}

/**
 * Renders the immutable local-or-Cloud placement choice for project creation.
 *
 * Args:
 *   props: Current placement, Cloud eligibility, and selection callbacks.
 *
 * Returns:
 *   The placement selector fieldset.
 *
 * Raises:
 *   Nothing.
 */
export function ProjectPlacementSelector({
  value,
  onChange,
  cloudStatus,
  onCloudSelect,
  localSelectable = true,
  localReason,
  onOpenLocalSetup,
  presentation = 'default',
}: ProjectPlacementSelectorProps): React.JSX.Element {
  const cloudSelectable = cloudStatus.state === 'eligible' || cloudStatus.state === 'signed-out';
  const cloudReason =
    cloudStatus.state === 'signed-out'
      ? 'Sign in with your Foundry Cloud account'
      : cloudStatus.state === 'ineligible'
        ? 'This account is not in the current Foundry Cloud cohort.'
        : cloudStatus.state === 'unavailable'
          ? 'Cloud placement is temporarily unavailable.'
          : 'Create and run this project in Cloud';
  return (
    <fieldset
      className={presentation === 'workbench' ? '' : 'mt-4'}
      data-testid="project-placement-selector"
    >
      <legend className="text-xs font-medium text-white/65">
        {presentation === 'workbench' ? 'Run on' : 'Run this project on:'}
      </legend>
      <div
        className={`mt-2 grid grid-cols-2 ${
          presentation === 'workbench'
            ? 'divide-x divide-white/[0.12] overflow-hidden rounded-xl border border-white/[0.12]'
            : 'gap-2'
        }`}
      >
        <button
          type="button"
          role="radio"
          aria-checked={value === 'local'}
          aria-disabled={!localSelectable}
          onClick={() => {
            if (localSelectable) onChange('local');
            else onOpenLocalSetup?.();
          }}
          title={localReason}
          className={`relative flex min-h-12 items-center gap-2 px-3 py-2 text-left text-sm transition-colors focus-visible:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-crucible-accent aria-disabled:cursor-not-allowed aria-disabled:opacity-40 ${
            presentation === 'workbench'
              ? value === 'local'
                ? 'z-10 bg-crucible-accent/[0.09] outline outline-1 -outline-offset-1 outline-crucible-accent'
                : 'bg-white/[0.025] hover:bg-white/[0.05] aria-disabled:hover:bg-white/[0.025]'
              : 'rounded-xl border'
          }`}
          style={{
            borderColor:
              presentation !== 'workbench'
                ? value === 'local'
                  ? 'var(--color-crucible-accent)'
                  : 'rgba(255,255,255,.1)'
                : undefined,
            background:
              presentation !== 'workbench'
                ? value === 'local'
                  ? 'rgba(139, 92, 246, .1)'
                  : 'rgba(255,255,255,.025)'
                : undefined,
            color: 'var(--text-1)',
          }}
        >
          <Laptop size={15} aria-hidden="true" />
          This Mac
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={value === 'cloud'}
          aria-disabled={!cloudSelectable}
          onClick={() => {
            if (cloudSelectable) void onCloudSelect();
          }}
          className={`relative flex min-h-12 items-center gap-2 px-3 py-2 text-left text-sm text-white/80 transition-colors focus-visible:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-crucible-accent aria-disabled:cursor-not-allowed aria-disabled:opacity-40 ${
            presentation === 'workbench'
              ? value === 'cloud'
                ? 'z-10 bg-crucible-accent/[0.09] outline outline-1 -outline-offset-1 outline-crucible-accent'
                : 'bg-white/[0.025] hover:bg-white/[0.05] aria-disabled:hover:bg-white/[0.025]'
              : 'rounded-xl border border-white/10 bg-white/[0.025]'
          }`}
          style={{
            // Keep the visible selection treatment aligned with the radio's accessible state.
            borderColor:
              presentation !== 'workbench'
                ? value === 'cloud'
                  ? 'var(--color-crucible-accent)'
                  : 'rgba(255,255,255,.1)'
                : undefined,
            background:
              presentation !== 'workbench'
                ? value === 'cloud'
                  ? 'rgba(139, 92, 246, .1)'
                  : 'rgba(255,255,255,.025)'
                : undefined,
          }}
          title={cloudReason}
        >
          <Cloud size={15} aria-hidden="true" />
          Cloud
        </button>
      </div>
      <p className="mt-2 text-xs leading-relaxed text-white/40">
        This project placement cannot be changed after creation.
        {!localSelectable && localReason ? ` This Mac: ${localReason}` : ''}
        {cloudStatus.state !== 'eligible' ? ` Cloud: ${cloudReason}` : ''}
      </p>
    </fieldset>
  );
}
